import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import { getEmployeeTimeCardGrouped, getTimecardRequests } from "../../../redux/features/EmployeeTimeCard";
import { getPayperiod } from "../../../redux/features/payperiod";
import {
    convertDecimalHoursToHM,
    formatTimeCardDate,
} from "../../../utils/employeeTimeCardhelper";
import { Loader, ArrowLeft, ArrowRight, Clock, CheckCircle } from "lucide-react";
import phpClient from "../../../network/phpClient";
import { toast } from "react-toastify";
import Select from "react-select";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import { getTimecardApprovalDetails } from "../../../redux/features/schedule/ScheduleApi";
import { ManagerTimesheetEmployee } from "../../../types";
import { setGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";
import { isPrivilege, UserPrivileges } from "../../../rules";
import { NgwAvatar } from "../../common";
import { getManagerEmployees } from "../../../redux/features/timesheet/api";
import ApprovalDetailsSection from "./ApprovalDetailsSection";
import RequestsAndAdjustmentsCard from "./RequestsAndAdjustmentsCard";
import { TimecardRequestItem } from "../../../types/response/timecard";
import MobileTimesheetEntryRow from "./MobileTimesheetEntryRow";
import { buildPayperiodOptions, resolveAutoSelectedPayperiod } from "../../../utils/payPeriodSelection";

interface ManagerEmployeeTimesheetDetailProps {
    employeeId?: string;
}

const ManagerEmployeeTimesheetDetail: React.FC<ManagerEmployeeTimesheetDetailProps> = ({
    employeeId: propEmployeeId,
}) => {
    const getRowBgClassForTimeoff = (apiColor?: string) => {
        let color = "red";
        if (apiColor) {
            const match = apiColor.match(/bg-([a-z]+)-\d+/);
            if (match && match[1]) {
                color = match[1];
            }
        }
        return `bg-${color}-50 hover:bg-${color}-100 cursor-default`;
    };

    const resolveRowBgClassForTimeoff = (entry: any) => {
        const defaultBgClass = "bg-gray-50 hover:bg-gray-100 cursor-pointer";
        if (entry?.record_type === "A" && entry?.other_details?.POLICY_COLOR) {
            return getRowBgClassForTimeoff(entry?.other_details?.POLICY_COLOR);
        }

        if(entry?.record_type === "T") {
            return getRowBgClassForTimeoff();
        }
        return defaultBgClass;
    };

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { employeeId: paramEmployeeId } = useParams(); // From URL if navigated directly
    const { setSourcePath, getSourcePath } = useNavigationHistory();
    // Use prop employeeId if provided, otherwise use from URL params
    // activeEmployeeId will be a string, which is what most APIs expect
    const activeEmployeeId = propEmployeeId || paramEmployeeId;

    const { groupedTimesheets } = useSelector((state: RootState) => state.employeeTimeCardData);
    const { payperiodFilters } = useSelector((state: RootState) => state.payperiod);
    const { employeeRecords } = useSelector((state: RootState) => state.timesheet);
    const [currentEmployee, setCurrentEmployee] = useState<ManagerTimesheetEmployee | null>(null);
    const user = useSelector((state: RootState) => state.user.user);
    const [selectedStartDate, setSelectedStartDate] = useState<string>("");
    const [selectedEndDate, setSelectedEndDate] = useState<string>("");
    const [selectedPayperiod, setSelectedPayperiod] = useState<string>(
        localStorage.getItem("selectedManagerPayperiod") || "",
    );
    const [payperiodOptions, setPayperiodOptions] = useState<any[]>([]);
    const prevStartDateRef = useRef<string>("");
    const prevEndDateRef = useRef<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [isFetchingEmployee, setIsFetchingEmployee] = useState<boolean>(false);
    const [timecardRequests, setTimecardRequests] = useState<TimecardRequestItem[]>([]);
    const hasAttemptedFetchRef = useRef<string>("");
    const hasInitializedPayperiodRef = useRef<boolean>(false);

    const isAdmin = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_ADMIN,
            }),
        [user?.employee_access_role_privileges],
    );

    const isAllowedToEdit = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges],
    );

    const isManagerPrivilege = useMemo(
        () =>
            isPrivilege({
                privileges: user?.employee_access_role_privileges,
                rule: UserPrivileges.EMPLOYEE_PRIV_UPDATE_TIMESHEET,
            }),
        [user?.employee_access_role_privileges],
    );
    const isManager = useMemo(() => user?.is_manager, [user?.is_manager]);
    const offCycleAllowed = !!(groupedTimesheets as any)?.data?.off_cycle_approval_allowed;

    const selfPrivilege = isAdmin || isAllowedToEdit;
    const managerPrivilege = isAdmin || isManagerPrivilege;

    const canEdit = isManager ? managerPrivilege : selfPrivilege;
    const canManageEntries = canEdit;

    const approvalStatus = (groupedTimesheets?.data?.approval_status || "").toLowerCase();
    const shouldShowAddEntryBtn = approvalStatus !== "processed";

    useEffect(() => {
        dispatch(setTopNavNameTitle("Timesheet Detail"));

        // Check if we came from notifications
        // Check location.state first, then check navigation history as fallback
        const fromNotificationsState =
            location.state?.from === "/m-notifications" ||
            location.state?.from === "/notificationDetail" ||
            location.state?.from === "/m-notification-detail";

        // Also check if previous route was a notification detail page
        const previousPath = getSourcePath();
        const fromNotificationDetail =
            previousPath === "/notificationDetail" || previousPath === "/m-notification-detail";

        const fromNotifications = fromNotificationsState || fromNotificationDetail;

        if (fromNotifications) {
            setSourcePath(`/m-notifications`);
            setGlobalBackHandler(() => {
                navigate(`/m-notifications`);
            });
        } else {
            setSourcePath(`/m-manager-timesheet`);
            setGlobalBackHandler(() => {
                navigate(`/m-manager-timesheet`, {
                    state: {
                        from: "/m-manager-timesheet",
                    },
                });
            });
        }
    }, [dispatch, navigate, setSourcePath, location.state, getSourcePath]);

    useEffect(() => {
        hasInitializedPayperiodRef.current = false;
    }, [activeEmployeeId, location.key]);

    useEffect(() => {
        // Find the current employee from records
        const match = employeeRecords?.find((emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId));

        if (match) {
            setCurrentEmployee(match);
            hasAttemptedFetchRef.current = ""; // Reset on successful match
        } else if (activeEmployeeId && hasAttemptedFetchRef.current !== activeEmployeeId) {
            // Employee not found in records, try to fetch it (only once per employee ID)
            setIsFetchingEmployee(true);
            hasAttemptedFetchRef.current = activeEmployeeId;
            const storedPayperiod = localStorage.getItem("selectedManagerPayperiod") || "";

            // Only fetch if we have a pay period, otherwise wait for pay period to be selected
            if (storedPayperiod) {
                const payload = {
                    pay_period: storedPayperiod,
                    employee_id: activeEmployeeId,
                    fields: ["total_hours"],
                };

                dispatch(getManagerEmployees(payload))
                    .then((result) => {
                        if (result.payload && Array.isArray(result.payload) && result.payload.length > 0) {
                            const foundEmployee = result.payload.find(
                                (emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId),
                            );
                            if (foundEmployee) {
                                setCurrentEmployee(foundEmployee);
                                hasAttemptedFetchRef.current = ""; // Reset on success
                            }
                        }
                        setIsFetchingEmployee(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching employee:", error);
                        setIsFetchingEmployee(false);
                        hasAttemptedFetchRef.current = ""; // Reset on error so we can retry
                    });
            } else {
                // No pay period yet, wait for it to be set
                setIsFetchingEmployee(false);
                hasAttemptedFetchRef.current = ""; // Reset so we can try again when pay period is available
            }
        }

        if (!selectedStartDate || !selectedEndDate) {
            setTimecardRequests([]);
        }
    }, [dispatch, activeEmployeeId, employeeRecords, selectedStartDate, selectedEndDate]);

    useEffect(() => {
        if (activeEmployeeId) {
            dispatch(getPayperiod({ employee_id: activeEmployeeId, count: -5 }));
        }
    }, [dispatch, activeEmployeeId]);

    useEffect(() => {
        if (!activeEmployeeId || !selectedStartDate || !selectedEndDate) {
            return;
        }

        setLoading(true);

        const apiCalls = [
            dispatch(
                getEmployeeTimeCardGrouped({
                    start_date: selectedStartDate,
                    end_date: selectedEndDate,
                    employee_id: activeEmployeeId,
                    approval_status: "",
                    sort_by: "os_emp_start_datetime",
                    sort_order: "ASC",
                    group_by: "date",
                    page: "1",
                }),
            ),
            dispatch(
                getTimecardApprovalDetails({
                    start_date: selectedStartDate,
                    end_date: selectedEndDate,
                    extra_filters: ["employees_list", "reject_list"],
                }),
            ),
            dispatch(
                getTimecardRequests({
                    start_date: selectedStartDate,
                    end_date: selectedEndDate,
                    employee_id: activeEmployeeId,
                }),
            ),
        ];

        Promise.all(apiCalls)
            .then(([, , requestsRes]: any[]) => {
                const data = requestsRes?.payload?.data;
                setTimecardRequests(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching employee data:", error);
                setTimecardRequests([]);
                setLoading(false);
            });
    }, [dispatch, activeEmployeeId, selectedStartDate, selectedEndDate]);

    // Retry fetching employee when pay period becomes available
    useEffect(() => {
        const storedPayperiod = localStorage.getItem("selectedManagerPayperiod") || "";
        if (
            storedPayperiod &&
            activeEmployeeId &&
            !currentEmployee &&
            hasAttemptedFetchRef.current !== activeEmployeeId &&
            !isFetchingEmployee
        ) {
            // Pay period is now available, try fetching employee again
            setIsFetchingEmployee(true);
            hasAttemptedFetchRef.current = activeEmployeeId;

            const payload = {
                pay_period: storedPayperiod,
                employee_id: activeEmployeeId,
                fields: ["total_hours"],
            };

            dispatch(getManagerEmployees(payload))
                .then((result) => {
                    if (result.payload && Array.isArray(result.payload) && result.payload.length > 0) {
                        const foundEmployee = result.payload.find(
                            (emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId),
                        );
                        if (foundEmployee) {
                            setCurrentEmployee(foundEmployee);
                            hasAttemptedFetchRef.current = "";
                        }
                    }
                    setIsFetchingEmployee(false);
                })
                .catch((error) => {
                    console.error("Error fetching employee:", error);
                    setIsFetchingEmployee(false);
                    hasAttemptedFetchRef.current = "";
                });
        }
    }, [dispatch, activeEmployeeId, currentEmployee, isFetchingEmployee, selectedPayperiod]);

    // useEffect(() => {
    //     if (currentEmployee?.emp_detail?.emp_name) {
    //         dispatch(setTopNavNameTitle(`Timesheet - ${currentEmployee.emp_detail.emp_name}`));
    //     }
    // }, [currentEmployee, dispatch]);

    useEffect(() => {
        if (payperiodFilters?.length) {
            const newPayperiodOptions = buildPayperiodOptions(payperiodFilters);
            const hasCurrentSelection = newPayperiodOptions.some((option) => option.value === selectedPayperiod);
            const resolvedPayperiod = hasInitializedPayperiodRef.current
                ? hasCurrentSelection
                    ? selectedPayperiod
                    : resolveAutoSelectedPayperiod({
                          payperiodOptions: newPayperiodOptions,
                          locationState: null,
                          storageKey: "selectedManagerPayperiod",
                          storageDateKey: "selectedManagerPayperiodDate",
                      })
                : resolveAutoSelectedPayperiod({
                      payperiodOptions: newPayperiodOptions,
                      locationState: location.state as
                          | { payPeriod?: string; payPeriodStart?: string; payPeriodEnd?: string; targetDate?: string }
                          | null,
                      storageKey: "selectedManagerPayperiod",
                      storageDateKey: "selectedManagerPayperiodDate",
                  });

            hasInitializedPayperiodRef.current = true;

            if (resolvedPayperiod) {
                if (resolvedPayperiod !== selectedPayperiod) {
                    setSelectedPayperiod(resolvedPayperiod);
                    localStorage.setItem("selectedManagerPayperiod", resolvedPayperiod);
                }
                const [startDatetime, endDatetime] = resolvedPayperiod.split(" - ");
                handlePayperiodChange(startDatetime, endDatetime);
            }

            setPayperiodOptions(newPayperiodOptions);
        }
    }, [location.state, payperiodFilters, selectedPayperiod]);

    const handlePayperiodChange = (startDate: string, endDate: string) => {
        if (startDate === prevStartDateRef.current && endDate === prevEndDateRef.current) {
            return;
        }

        setLoading(true);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        prevStartDateRef.current = startDate;
        prevEndDateRef.current = endDate;

    };

    const handlePayperiodDropdownChange = (selectedOption: any) => {
        setSelectedPayperiod(selectedOption?.value);
        localStorage.setItem("selectedManagerPayperiod", selectedOption?.value);
        const [startDatetime, endDatetime] = selectedOption.value.split(" - ");
        handlePayperiodChange(startDatetime, endDatetime);
    };

    const status = (groupedTimesheets?.data?.approval_status || "").toLowerCase();
    const shouldApprovalBtn = status === "pending";

    // This is redundant as we've already set up the back handler in useEffect

    if (!activeEmployeeId) {
        return (
            <div className="container mx-auto bg-white p-4">
                <div className="text-center text-gray-500">No employee selected or employee not found.</div>
            </div>
        );
    }

    // Show loading if we're fetching the employee or if we don't have the employee yet
    if (isFetchingEmployee || !currentEmployee) {
        return (
            <div className="container mx-auto bg-white p-4">
                <div className="flex justify-center items-center py-8">
                    <Loader size={32} color="#2563eb" strokeWidth={2.5} absoluteStrokeWidth className="animate-spin" />
                </div>
                <div className="text-center text-gray-500 mt-4">Loading employee data...</div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto bg-white">
            <div className="flex flex-col text-gray-800 text-base font-sans">
                {/* Header with navigation */}
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => {
                                if (!employeeRecords?.length) return;
                                const currentIndex = employeeRecords.findIndex(
                                    (emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId),
                                );
                                console.log("Current index:", currentIndex);

                                let targetIndex;
                                if (currentIndex > 0) {
                                    targetIndex = currentIndex - 1;
                                } else {
                                    targetIndex = employeeRecords.length - 1;
                                }

                                const targetEmployee = employeeRecords[targetIndex];
                                if (targetEmployee?.emp_detail?.employee_id) {
                                    console.log("Navigating to:", targetEmployee.emp_detail.employee_id);
                                    navigate(`/m-manager-timesheet/${targetEmployee.emp_detail.employee_id}`, {
                                        state: {
                                            payPeriod: selectedPayperiod,
                                            payPeriodStart: selectedStartDate,
                                            payPeriodEnd: selectedEndDate,
                                        },
                                    });
                                }
                            }}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                            disabled={!employeeRecords || employeeRecords.length <= 1}
                        >
                            <ArrowLeft
                                size={20}
                                className={
                                    !employeeRecords || employeeRecords.length <= 1 ? "text-gray-300" : "text-blue-600"
                                }
                            />
                        </button>
                    </div>
                    <div className="flex items-center">
                        <div className="mr-4">
                            <NgwAvatar
                                src={currentEmployee.emp_detail?.profile_image_url}
                                name={currentEmployee.emp_detail?.emp_name}
                                className="w-12 h-12"
                            />
                        </div>
                        <div className="flex flex-col">
                            <div className="text-lg font-medium text-gray-900">
                                {currentEmployee.emp_detail?.emp_name || "Unknown"}
                            </div>
                            <div className="text-sm text-gray-500">
                                {currentEmployee.department?.department_name || "No dept"}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => {
                                if (!employeeRecords?.length) return;
                                const currentIndex = employeeRecords.findIndex(
                                    (emp) => String(emp.emp_detail?.employee_id) === String(activeEmployeeId),
                                );
                                console.log("Current index:", currentIndex);

                                let targetIndex;
                                if (currentIndex < employeeRecords.length - 1) {
                                    targetIndex = currentIndex + 1;
                                } else {
                                    targetIndex = 0;
                                }

                                const targetEmployee = employeeRecords[targetIndex];
                                if (targetEmployee?.emp_detail?.employee_id) {
                                    console.log("Navigating to:", targetEmployee.emp_detail.employee_id);
                                    navigate(`/m-manager-timesheet/${targetEmployee.emp_detail.employee_id}`, {
                                        state: {
                                            payPeriod: selectedPayperiod,
                                            payPeriodStart: selectedStartDate,
                                            payPeriodEnd: selectedEndDate,
                                        },
                                    });
                                }
                            }}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                            disabled={!employeeRecords || employeeRecords.length <= 1}
                        >
                            <ArrowRight
                                size={20}
                                className={
                                    !employeeRecords || employeeRecords.length <= 1 ? "text-gray-300" : "text-blue-600"
                                }
                            />
                        </button>

                        {/* <button
                            onClick={() => navigate("/m-manager-timesheet")}
                            className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                        >
                            <Users size={20} className="text-blue-600" />
                        </button> */}
                    </div>
                </div>

                {/* Pay Period Selector */}
                <div className="px-4 pt-3">
                    <Select
                        value={payperiodOptions?.find((option) => option?.value === selectedPayperiod)}
                        onChange={handlePayperiodDropdownChange}
                        options={payperiodOptions}
                        className="block w-full"
                        classNamePrefix="react-select"
                        isSearchable={false}
                        placeholder="Select a pay period"
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        styles={{
                            control: (baseStyles) => ({
                                ...baseStyles,
                                borderColor: "#e5e7eb",
                                backgroundColor: "#f9fafb",
                                ":hover": {
                                    borderColor: "#e5e7eb",
                                },
                            }),
                            option: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: state.isSelected ? "#2563eb" : state.isFocused ? "#eff6ff" : "white",
                                color: state.isSelected ? "white" : "#374151",
                            }),
                        }}
                    />
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center py-6">
                        <Loader
                            size={32}
                            color="#2563eb"
                            strokeWidth={2.5}
                            absoluteStrokeWidth
                            className="animate-spin"
                        />
                    </div>
                )}

                {/* Timesheet Content */}
                {!loading && (
                    <>
                        {/* Summary Cards - Responsive Grid */}
                        <div className="px-4 py-3">
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6 mb-8">
                                {/* Today's Summary */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-blue-600 mb-2">
                                            {/* {convertDecimalHoursToHM(todaySummary?.total_hours ?? 0)} */}
                                            {/* {convertDecimalHoursToHM(groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0)} */}
                                            {groupedTimesheets?.data?.today_totals?.total_hours_string ?? 0}
                                        </div>
                                        <div className="text-lg font-medium text-gray-900 mb-2">Today</div>
                                    </div>
                                </div>
                                
                                {/* Pay Period Summary */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-green-600 mb-2">
                                            {/* {convertDecimalHoursToHM(
                                                groupedTimesheets?.data?.pay_period_totals?.total_hours ?? 0
                                            )} */}
                                            {groupedTimesheets?.data?.pay_period_totals?.total_hours_string ?? "0"}
                                        </div>
                                        <div className="text-lg font-medium text-gray-900 mb-2">Pay Period</div>
                                        <div className="text-sm text-gray-600">Total hours this period</div>
                                    </div>
                                </div>
                                {/* Approval Status Card */}
                                {groupedTimesheets?.data?.approval_details &&
                                    groupedTimesheets.data.approval_details.length > 0 && (
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                                            <div className="text-center">
                                                <div className="text-lg font-medium text-gray-900 mb-3">
                                                    Approval Status
                                                </div>
                                                <div className="space-y-2">
                                                    {(() => {
                                                        const approvalStatus = (
                                                            groupedTimesheets.data.approval_status || ""
                                                        ).toUpperCase();
                                                        const isProcessed = approvalStatus === "PROCESSED";
                                                        const approvedCount =
                                                            groupedTimesheets.data.approval_details.filter((a) => {
                                                                const statusCode = a.current_status_code_txn;
                                                                return (
                                                                    statusCode !== undefined &&
                                                                    statusCode !== null &&
                                                                    (statusCode === 1 || Number(statusCode) === 1)
                                                                );
                                                            }).length;
                                                        const totalCount =
                                                            groupedTimesheets.data.approval_details.length;
                                                        const allApproved = approvedCount === totalCount;

                                                        return (
                                                            <>
                                                                <div className="flex items-center justify-center space-x-2 whitespace-nowrap">
                                                                    {isProcessed || allApproved ? (
                                                                        <>
                                                                            <CheckCircle
                                                                                className="text-green-600 flex-shrink-0"
                                                                                size={20}
                                                                            />
                                                                            <span className="text-green-600 font-medium">
                                                                                Processed
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Clock
                                                                                className="text-blue-600 flex-shrink-0"
                                                                                size={20}
                                                                            />
                                                                            <span className="text-blue-600 font-medium">
                                                                                In Progress
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="text-sm text-gray-700">
                                                                    <span className="text-blue-600 font-medium">
                                                                        {approvedCount}
                                                                    </span>{" "}
                                                                    of{" "}
                                                                    <span className="text-blue-600 font-medium">
                                                                        {totalCount}
                                                                    </span>{" "}
                                                                    approved
                                                                </div>
                                                                {!isProcessed && !allApproved && (
                                                                    <div className="text-sm text-gray-600">
                                                                        Waiting{" "}
                                                                        <span className="text-blue-600">
                                                                            on{" "}
                                                                            {(() => {
                                                                                const pendingApproval =
                                                                                    groupedTimesheets.data.approval_details.find(
                                                                                        (a) => {
                                                                                            const statusCode =
                                                                                                a.current_status_code_txn;
                                                                                            return (
                                                                                                statusCode !==
                                                                                                    undefined &&
                                                                                                statusCode !== null &&
                                                                                                (statusCode === 0 ||
                                                                                                    Number(
                                                                                                        statusCode,
                                                                                                    ) === 0)
                                                                                            );
                                                                                        },
                                                                                    );
                                                                                const approverNames =
                                                                                    pendingApproval?.wf_task_actor?.EMPLOYEE?.map(
                                                                                        (emp) => emp.employee_name,
                                                                                    ).join(", ") || "Approver";
                                                                                return approverNames;
                                                                            })()}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Action Card */}
                                <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-6 rounded-lg border border-gray-200">
                                    <div className="text-center">
                                        <div className="text-lg font-medium text-gray-900 mb-4">Actions</div>
                                        {canManageEntries && shouldShowAddEntryBtn && (
                                            <div>
                                                <button
                                                    className="mb-3 flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 transition-colors duration-200 whitespace-normal break-words"
                                                    onClick={() => {
                                                        const managerReturnPath = `/m-manager-timesheet/${activeEmployeeId}`;
                                                        navigate("/m-add-timesheet-entry", {
                                                            state: {
                                                                employeeId: activeEmployeeId,
                                                                employeeName: currentEmployee.emp_detail?.emp_name,
                                                                isManager: true,
                                                                mode: "create",
                                                                managerReturnPath,
                                                                managerView: {
                                                                    department: currentEmployee.department?.name,
                                                                    departmentCode: currentEmployee.department?.code,
                                                                },
                                                            },
                                                        });
                                                    }}
                                                >
                                                    Add Entry
                                                </button>
                                            </div>
                                        )}

                                        {offCycleAllowed && (
                                            <div className="mt-2">
                                                <button
                                                    className="mb-3 flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 transition-colors duration-200 whitespace-normal break-words"
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            const payload = {
                                                                start_date: selectedStartDate,
                                                                end_date: selectedEndDate,
                                                                APPROVAL_STATUS: 1,
                                                                APPROVAL_NOTE: "",
                                                                EMP_IDS: [activeEmployeeId],
                                                                WF_CODE: (groupedTimesheets as any)?.data?.WF_CODE,
                                                            };

                                                            await phpClient.post({
                                                                url: "/timecard/approval_off_cycle",
                                                                data: payload,
                                                            });
                                                            toast.success("Off-cycle approval request sent");

                                                            // Refresh grouped timesheet and approval details
                                                            await Promise.all([
                                                                dispatch(
                                                                    getEmployeeTimeCardGrouped({
                                                                        start_date: selectedStartDate,
                                                                        end_date: selectedEndDate,
                                                                        employee_id: activeEmployeeId,
                                                                        approval_status: "",
                                                                        sort_by: "os_emp_start_datetime",
                                                                        sort_order: "ASC",
                                                                        group_by: "date",
                                                                        page: "1",
                                                                    }),
                                                                ),
                                                                dispatch(
                                                                    getTimecardApprovalDetails({
                                                                        start_date: selectedStartDate,
                                                                        end_date: selectedEndDate,
                                                                        extra_filters: [
                                                                            "employees_list",
                                                                            "reject_list",
                                                                        ],
                                                                    }),
                                                                ),
                                                            ]);
                                                        } catch (error) {
                                                            console.error("approval_off_cycle error", error);
                                                            toast.error("Failed to send off-cycle approval request");
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }}
                                                >
                                                    Approve Off-Cycle
                                                </button>
                                            </div>
                                        )}

                                        {shouldApprovalBtn && (
                                            <button
                                                className="flex w-full min-w-0 items-center justify-center rounded-lg border border-blue-600 bg-transparent px-4 py-3 text-center text-sm font-medium leading-tight text-blue-600 whitespace-normal break-words"
                                                onClick={() => {
                                                    navigate(
                                                        `/m-timesheet-approval/${selectedStartDate}/${selectedEndDate}/${activeEmployeeId}`,
                                                    );
                                                }}
                                            >
                                                Approval
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Timesheet Entries */}
                        <div className="px-4 pb-4" id="timesheet-entries">
                            {groupedTimesheets?.data?.timesheet_entries_by_date &&
                            Object.keys(groupedTimesheets.data.timesheet_entries_by_date).length === 0 ? (
                                <div className="text-center text-gray-500 py-4">
                                    No timecard data available for this period.
                                </div>
                            ) : (
                                Object.entries(groupedTimesheets?.data?.timesheet_entries_by_date || {}).map(
                                    ([dateKey, entries]) => {
                                        const totalHours = entries.reduce(
                                            (acc, entry) => acc + (Number(entry?.hours) || 0),
                                            0,
                                        );
                                        return (
                                            <div key={dateKey} className="border-t flex items-start mb-4 py-1">
                                                <div className="w-2/6 font-medium text-lg text-blue-500">
                                                    {formatTimeCardDate(dateKey)}
                                                </div>
                                                <div className="w-4/6">
                                                    <div className="text-lg text-gray-500 font-medium">
                                                        {convertDecimalHoursToHM(totalHours)}
                                                    </div>
                                                    {entries.map((entry: any, index: number) => {
                                                        const isAttendance = entry?.record_type === "A";

                                                        return (
                                                            <MobileTimesheetEntryRow
                                                                key={index}
                                                                entry={entry}
                                                                rowClassName={resolveRowBgClassForTimeoff(entry)}
                                                                onClick={() => {
                                                                    if (isAttendance) {
                                                                        navigate(
                                                                            `/m-manager-timecard-record-detail/${entry.id}`,
                                                                            {
                                                                                state: {
                                                                                    employeeId: activeEmployeeId,
                                                                                    recordData: entry,
                                                                                    mode: "view",
                                                                                },
                                                                            },
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    },
                                )
                            )}
                        </div>

                        <RequestsAndAdjustmentsCard requests={timecardRequests} loading={loading} />

                        {/* Approval Details Section */}
                        <div className="px-4 pb-16">
                            <ApprovalDetailsSection approvalDetails={groupedTimesheets?.data?.approval_details || []} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ManagerEmployeeTimesheetDetail;
